/**
 * CitrusRevealBuildUp - UX-6, the pre-reveal build-up sequence (USCN).
 *
 * After the final answer: a 3 to 4 second staged sequence. Each line fades in
 * over a citrus mark growing stage by stage, then the quiz moves on to the
 * email gate. This is anticipation, not a loading screen: it runs even though
 * results are instant.
 */
import React, { useEffect, useState } from 'react';
import { CITRUS_BUILD_UP_LINES } from '../../data/citrusQuizUxCopy';
import { CitrusGrowthStage } from './CitrusQuizIcons';

const LINE_MS = 1000;
/** Extra beat after the last line before advancing. */
const EXIT_BEAT_MS = 350;
const DUAL_STAGE_LINE_INDEX = 2;
const DUAL_STAGE_START = 3;

interface CitrusRevealBuildUpProps {
  onComplete: () => void;
}

const CitrusRevealBuildUp: React.FC<CitrusRevealBuildUpProps> = ({ onComplete }) => {
  const [lineIndex, setLineIndex] = useState(0);
  const [stage, setStage] = useState<number>(CITRUS_BUILD_UP_LINES[0].stage);

  useEffect(() => {
    const timers: number[] = [];

    CITRUS_BUILD_UP_LINES.forEach((entry, index) => {
      if (index === 0) return;
      timers.push(
        window.setTimeout(() => {
          setLineIndex(index);
          // Why this matters: line three starts at the sprout before adding leaves.
          setStage(index === DUAL_STAGE_LINE_INDEX ? DUAL_STAGE_START : entry.stage);
        }, index * LINE_MS)
      );
    });

    // Line 3 covers two stages: the sprout appears at its boundary, then leaves
    // appear halfway through. This keeps all five stages visible in order.
    timers.push(
      window.setTimeout(
        () => setStage(CITRUS_BUILD_UP_LINES[DUAL_STAGE_LINE_INDEX].stage),
        DUAL_STAGE_LINE_INDEX * LINE_MS + LINE_MS / 2
      )
    );

    timers.push(
      window.setTimeout(() => {
        onComplete();
      }, CITRUS_BUILD_UP_LINES.length * LINE_MS + EXIT_BEAT_MS)
    );

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [onComplete]);

  const currentLine = CITRUS_BUILD_UP_LINES[lineIndex].line;
  const isFinalStage = stage === 5;

  return (
    <div className="citrus-buildup-screen">
      <div className="citrus-buildup-card">
        <div
          className={`citrus-buildup-visual ${isFinalStage ? 'citrus-buildup-visual-pulse' : ''}`}
          aria-hidden="true"
        >
          <CitrusGrowthStage stage={stage} />
        </div>
        <p key={currentLine} className="citrus-buildup-line" role="status" aria-live="polite">
          {currentLine}
        </p>
      </div>
    </div>
  );
};

export default CitrusRevealBuildUp;
